import { Box, Button, DialogTitle } from '@material-ui/core';
import i18n from 'i18n';
import useStyles from 'pages/assignments/components/DialogForm/styles';
import SimpleFields from 'pages/stories/components/Form/SimpleFields';
import { StoryFormData } from 'pages/stories/interface';
import validateForm from 'pages/stories/utils';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import { isFormValid, updateFormData } from 'utils/form';

interface StoryFormI {
  onSave: (formData: StoryFormData) => void;
  onCancel?: () => void;
  defaultState?: { title: string; script: string; text: string };
}

const StoryForm: FunctionComponent<StoryFormI> = ({
  onSave,
  onCancel,
  defaultState
}: StoryFormI) => {
  const classes = useStyles();

  const { storyTypes, storyGenres, locations, storyStates } = useSelector(
    (state: $Lns.DefaultState) => state.default
  );

  const initialState = {
    title: defaultState?.title || '',
    slug: '',
    type: storyTypes.length ? storyTypes[0].guid : '',
    plannedDuration: 0,
    calculatedDuration: 0,
    notes: '',
    text: defaultState?.text || '',
    script: defaultState?.script || '',
    storyState: storyStates.length ? storyStates[0].guid : '',
    location: locations.length ? locations[0].guid : '',
    genre: storyGenres.length ? storyGenres[0].guid : '',
    errors: {
      title: '',
      slug: '',
      type: ''
    }
  };

  const [formData, setFormData] = useState<StoryFormData>(initialState);

  const onChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    const formDataInput = capsTitles(formData);
    const updatedFormData = updateFormData(e, formDataInput, validateForm);
    setFormData(updatedFormData);
  };

  const capsTitles = (data: StoryFormData) => {
    const updateData = { ...data };
    updateData.slug = data.slug.toLocaleUpperCase();
    updateData.title = data.title.toLocaleUpperCase();
    return updateData;
  };

  const onChangeType = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value, name } = e.target;

    setFormData({
      ...formData,
      [name || '']: value
    });
  };

  const btnDisabled = !isFormValid(
    ['slug', 'title', 'type', 'genre', 'location'],
    formData
  );

  return (
    <Box display="flex" flexDirection="Column" gridGap={16}>
      <DialogTitle id="create-story-title" className={classes.dialogTitle}>
        {i18n.t('pages.stories.dialog.title')}
      </DialogTitle>

      <SimpleFields
        formData={formData}
        storyTypes={storyTypes}
        storyGenres={storyGenres}
        locations={locations}
        onChangeField={onChangeField}
        onChangeType={onChangeType}
      />

      <Box display="flex" gridGap={16} justifyContent="flex-end">
        <Button color="secondary" variant="outlined" onClick={onCancel}>
          {i18n.t('button.cancel')}
        </Button>
        <Button
          data-test-id="save-btn"
          disabled={btnDisabled}
          color="secondary"
          variant="contained"
          onClick={() => onSave(formData)}
        >
          {i18n.t('button.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default StoryForm;

StoryForm.defaultProps = {
  onCancel: undefined,
  defaultState: undefined
};
